import React, { useEffect, useState } from 'react';
import { TextField, Typography, Divider } from '@mui/material';
import { useWebSocket } from '../WebSocketContext';

// Theme colors - matching Sidebar
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const selectedColor = '#4545a0';
const lightPurple = '#f0f0ff';

// Backend extractors prefix file-backed eventdata with `filename:` for traceability.
// The meta view hides that wrapper so users see the actual content first.
const stripLeadingFilenameLine = (value) => {
  const text = value == null ? '' : String(value);
  return text.replace(/^filename:\s*[^\r\n]*(?:\r?\n)?/i, '');
};

const extractEditableMetadataText = (value) => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'object') {
    if (!Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value')) {
      return extractEditableMetadataText(value.value);
    }

    return JSON.stringify(value, null, 2);
  }

  const text = stripLeadingFilenameLine(value);
  const trimmed = text.trim();

  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.prototype.hasOwnProperty.call(parsed, 'value')) {
        return extractEditableMetadataText(parsed.value);
      }
      if (typeof parsed === 'string') {
        return stripLeadingFilenameLine(parsed).replace(/\\n/g, '\n');
      }
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      // Fall through to raw text if the value is not valid JSON.
    }
  }

  return text.replace(/\\n/g, '\n');
};

function TimeEventViewMeta({ timeevent, editable = true }) {
  const { ws } = useWebSocket();
  const [isEditing, setIsEditing] = useState(false);
  const [editableEventData, setEditableEventData] = useState(() =>
    extractEditableMetadataText(timeevent?.eventdata)
  );

  useEffect(() => {
    setEditableEventData(extractEditableMetadataText(timeevent?.eventdata));
  }, [timeevent?.eventdata]);

  const updateMeta = (newMeta) => {
    ws.send(JSON.stringify({
      id: timeevent.id, // id passed correctly
      data: newMeta,
      command: "updatemetadata"
    }));
  };

  const handleBlur = () => {
    if (!editable) {
      setIsEditing(false);
      return;
    }
    // Send ws call "updatemetadata" using the ws from context
    if (ws && typeof ws.send === 'function') {
      updateMeta(editableEventData);
    }
    setIsEditing(false);
  };

  const handleChange = (e) => {
    setEditableEventData(e.target.value);
  };

  const isSoundFile = timeevent.eventsource && (timeevent.eventsource.endsWith('.wav') || timeevent.eventsource.endsWith('.m4a') || timeevent.eventsource.endsWith('.mp3'));
  const title = isSoundFile ? "Transcription" : "Meta Data";

  return (
    <div style={{ marginTop: '10px' }}>
      <Divider style={{ marginBottom: '10px' }} />
      <Typography variant="subtitle2" gutterBottom 
        style={{ 
          fontSize: '0.75rem', 
          color: primaryColor, 
          fontWeight: 'bold' 
        }}
      >
        {title}
      </Typography>
      {isEditing ? 
        <TextField
          value={editableEventData}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          multiline
          sx={{ 
            backgroundColor: lightPurple,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: `${primaryColor}40`,
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: selectedColor,
              },
            },
            fontSize: '0.75rem' 
          }}
        />
      : 
        <Typography
          variant="body2"
          onClick={editable ? () => setIsEditing(true) : undefined}
          sx={{
            cursor: editable ? 'pointer' : 'default',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textOverflow: 'ellipsis',
            fontSize: '0.75rem',
            color: '#555',
            backgroundColor: `${lightPurple}60`,
            padding: '8px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            ...(editable ? {
              '&:hover': {
                backgroundColor: lightPurple,
              },
            } : {}),
            '@media (max-width: 600px)': {
              fontSize: '0.7rem',
            },
          }}
        >
          {editableEventData}
        </Typography>
      }
    </div>
  );
}

export default TimeEventViewMeta;
